import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  cn,
  Icon,
  Input,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  UserAvatar,
} from "@nexus/ui";
import { changelogRecipientsQueryOptions } from "@/lib/queries";
import type { ChangelogRecipient } from "@/features/changelog/api/changelog.api";
import {
  useAddChangelogRecipient,
  useRemoveChangelogRecipient,
  useSetChangelogRecipientActive,
} from "@/features/changelog/hooks/useChangelogActions";
import type { ChangelogPermissions } from "@/features/changelog/hooks/useChangelogPermissions";
import { changelogErrorMessage } from "@/features/changelog/lib/changelog-errors";

interface ChangelogRecipientsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  permissions: ChangelogPermissions;
}

/**
 * The changelog's own mailing list, kept here rather than in a config file so
 * the person who decides who hears about a release is the person who can change
 * it. The Adge team is on it by default and stays in step with access, so the
 * only editing most releases need is switching somebody off. Paused recipients
 * stay on the list: someone on leave is a different case from someone who left.
 */
export function ChangelogRecipientsSheet({
  isOpen,
  onOpenChange,
  permissions,
}: ChangelogRecipientsSheetProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const { data, isPending, isError } = useQuery({
    ...changelogRecipientsQueryOptions(),
    enabled: isOpen && permissions.canListRecipients,
  });

  const addRecipient = useAddChangelogRecipient();
  const setActive = useSetChangelogRecipientActive();
  const removeRecipient = useRemoveChangelogRecipient();

  const isBusy = addRecipient.isPending || setActive.isPending || removeRecipient.isPending;
  const writeError = addRecipient.error ?? setActive.error ?? removeRecipient.error;

  const recipients = data?.recipients ?? [];
  const team = recipients.filter((recipient) => recipient.source === "TEAM");
  const manual = recipients.filter((recipient) => recipient.source === "MANUAL");
  const activeCount = recipients.filter((recipient) => recipient.isActive).length;

  function handleAdd(event: FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;

    addRecipient.mutate(
      { email: email.trim(), name: name.trim() || null },
      {
        onSuccess: () => {
          setEmail("");
          setName("");
        },
      },
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle>{t("admin.changelog.recipients.title", "Changelog mailing list")}</SheetTitle>
          <SheetDescription>
            {recipients.length > 0
              ? t("admin.changelog.recipients.summary", {
                  defaultValue: "{{active}} of {{total}} people receive an approved roundup.",
                  active: activeCount,
                  total: recipients.length,
                })
              : t(
                  "admin.changelog.recipients.description",
                  "Everyone active here receives the release notes when an approved changelog is emailed.",
                )}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-5">
          {permissions.canAddRecipient ? (
            <form className="flex flex-col gap-2" onSubmit={handleAdd}>
              <div className="flex gap-2">
                <Input
                  type="email"
                  required
                  value={email}
                  placeholder={t("admin.changelog.recipients.emailPlaceholder", "name@adge.ai")}
                  aria-label={t("admin.changelog.recipients.email", "Email address")}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <Input
                  value={name}
                  className="w-40"
                  placeholder={t("admin.changelog.recipients.namePlaceholder", "Name (optional)")}
                  aria-label={t("admin.changelog.recipients.name", "Name")}
                  onChange={(event) => setName(event.target.value)}
                />
                <Button
                  type="submit"
                  size="sm"
                  iconStart="plus"
                  disabled={isBusy || email.trim().length === 0}
                >
                  {t("admin.changelog.recipients.add", "Add")}
                </Button>
              </div>
            </form>
          ) : null}

          {writeError ? (
            <div className="rounded-lg border border-error-border bg-error-subtle px-4 py-3 text-xs text-error-subtle-foreground">
              {changelogErrorMessage(
                writeError,
                t("admin.changelog.recipients.writeError", "Could not update the mailing list."),
              )}
            </div>
          ) : null}

          {!permissions.canListRecipients ? (
            <p className="m-0 text-xs text-muted-foreground">
              {t(
                "admin.changelog.recipients.noAccess",
                "You do not have access to the changelog mailing list.",
              )}
            </p>
          ) : isPending ? (
            <p className="m-0 text-xs text-muted-foreground">
              {t("admin.changelog.recipients.loading", "Loading the mailing list…")}
            </p>
          ) : isError ? (
            <p className="m-0 text-xs text-muted-foreground">
              {t("admin.changelog.recipients.loadError", "Could not load the mailing list.")}
            </p>
          ) : data.recipients.length === 0 ? (
            <p className="m-0 text-xs text-muted-foreground">
              {t(
                "admin.changelog.recipients.empty",
                "Nobody is on the list yet, so the email channel stays unavailable.",
              )}
            </p>
          ) : (
            <>
              <RecipientGroup
                title={t("admin.changelog.recipients.teamGroup", "Adge team")}
                caption={t(
                  "admin.changelog.recipients.teamCaption",
                  "Everyone with Adge access is on the list by default. Switch someone off to skip them.",
                )}
                recipients={team}
                isBusy={isBusy}
                permissions={permissions}
                onSetActive={(recipient, isActive) =>
                  setActive.mutate({ email: recipient.email, isActive })
                }
                onRemove={null}
              />
              {manual.length > 0 ? (
                <RecipientGroup
                  title={t("admin.changelog.recipients.guestGroup", "Added by hand")}
                  caption={t(
                    "admin.changelog.recipients.guestCaption",
                    "Addresses outside Adge access, added here on purpose.",
                  )}
                  recipients={manual}
                  isBusy={isBusy}
                  permissions={permissions}
                  onSetActive={(recipient, isActive) =>
                    setActive.mutate({ email: recipient.email, isActive })
                  }
                  onRemove={(recipient) =>
                    recipient.id === null ? undefined : removeRecipient.mutate(recipient.id)
                  }
                />
              ) : null}
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

interface RecipientGroupProps {
  title: string;
  caption: string;
  recipients: ChangelogRecipient[];
  isBusy: boolean;
  permissions: ChangelogPermissions;
  onSetActive: (recipient: ChangelogRecipient, isActive: boolean) => void;
  /** Null where removal makes no sense — a team address would come straight back. */
  onRemove: ((recipient: ChangelogRecipient) => void) | null;
}

function RecipientGroup({
  title,
  caption,
  recipients,
  isBusy,
  permissions,
  onSetActive,
  onRemove,
}: RecipientGroupProps) {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <h3 className="m-0 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        <p className="m-0 text-[11px] text-muted-foreground/80">{caption}</p>
      </div>

      <ul className="m-0 flex list-none flex-col divide-y divide-border overflow-hidden rounded-lg border border-border p-0">
        {recipients.map((recipient) => (
          <li key={recipient.email} className="flex items-center gap-3 px-3 py-2.5">
            <span
              className={cn(
                "flex min-w-0 flex-1 items-center gap-3 transition-opacity duration-150",
                !recipient.isActive && "opacity-50",
              )}
            >
              <UserAvatar name={recipient.name ?? recipient.email} size="sm" />

              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-xs text-foreground">
                  {recipient.name ?? recipient.email}
                </span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {recipient.name ? recipient.email : null}
                  {recipient.addedByName
                    ? t("admin.changelog.recipients.addedBy", " · added by {{by}}", {
                        by: recipient.addedByName,
                      })
                    : null}
                </span>
              </span>
            </span>

            <Switch
              checked={recipient.isActive}
              disabled={isBusy || !permissions.canUpdateRecipient}
              aria-label={t("admin.changelog.recipients.toggle", "Receiving release notes")}
              onCheckedChange={(isActive) => onSetActive(recipient, isActive)}
            />

            {onRemove && permissions.canRemoveRecipient ? (
              <Button
                variant="ghost"
                size="icon"
                disabled={isBusy}
                aria-label={t("admin.changelog.recipients.remove", "Remove {{email}}", {
                  email: recipient.email,
                })}
                onClick={() => onRemove(recipient)}
              >
                <Icon name="trash-2" size="sm" color="inherit" aria-hidden />
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
